import $ from 'jquery';
import { events } from '../../Constants';
import { BannerManager, Type, Priority } from '../../components/BannerManager';

let instance = null;

export default class CartRequests {
  constructor() {
    if (!instance) {
      instance = this;
    } else {
      return instance;
    }

    this.$window = $(window);
    this.interval = null;
    this.handlerHelpers = {};
    this.postRequests = this._postRequests.bind(this);
    this.handleResponse = this._handleResponse.bind(this);
    this.handleError = this._handleError.bind(this);
    
    this.additionalHandlers = {
      onResponse: null,
      onError: null,
    };

    this.lastRequestTimestamp = Math.floor(Date.now());
    this.postAfterLastRequestMillis = 1000;

    this.noteRequest = null;
    this.itemRequests = [];
    this.inProgressItemRequests = [];

    this.triggerCartReload = false;
    this.recentItem = null;
    this.lockSubmission = false;

    this.bannerManager = new BannerManager();

    this._init();

    return instance;
  }

  submitItemRequest(variant, quantity, triggerCartReload = false) {
    let itemRequest = this._findExistingItemRequest(variant);

    if (itemRequest) {
      itemRequest.quantity = quantity;
    } else {
      itemRequest = {
        variant,
        quantity,
      };

      this.itemRequests.push(itemRequest);
    }

    itemRequest.quantity = Math.max(0, itemRequest.quantity);

    this.itemsRemoving = this._countItemsRemoving();
    this.triggerCartReload = this.triggerCartReload || triggerCartReload;
    this.recentItem = triggerCartReload ? variant : this.recentItem;
    this.lastRequestTimestamp = Math.floor(Date.now());

    this._triggerStartLoadingEvents();
  }

  submitNoteRequest(note) {
    this.noteRequest = note;
    this.lastRequestTimestamp = Math.floor(Date.now());
    this._triggerStartLoadingEvents();
  }

  unload() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  setHandlers(responseHandlers) {
    instance.handlerHelpers = $.extend({}, instance.additionalHandlers, responseHandlers);
  }
  
  restoreHandlers() {
    this.handlerHelpers = this.additionalHandlers;
  }

  _init() {
    this.interval = setInterval(this.postRequests, this.postAfterLastRequestMillis);
  }

  _countItemsRemoving() {
    return this.itemRequests.reduce((removing, itemRequest) => {
      const quantity = itemRequest.quantity;

      return quantity === 0 ? removing + 1 : removing;
    }, 0);
  }

  _triggerStartLoadingEvents() {
    this.itemRequests.forEach((itemRequest) => {
      const request = itemRequest;

      this.$window.trigger(
        events.cart.item.loadingStart,
        { request },
      );
    });

    if (this.note) {
      const request = this.note;

      this.$window.trigger(
        events.cart.note.loadingStart,
        { request },
      );
    }

    this.$window.trigger(
      events.cart.loadingStart,
      {
        affectsTotal: this.itemRequests.length,
        reload: this.triggerCartReload,
        removing: this.itemsRemoving,
      },
    );
  }

  _triggerFinishLoadingEvent(cartResponse, itemResponses, error = false) {
    itemResponses.forEach((itemResponse) => {
      const response = itemResponse;

      this.$window.trigger(
        events.cart.item.loadingFinish,
        { response },
      );
    });

    this.$window.trigger(events.cart.note.loadingFinish);

    this.$window.trigger(
      events.cart.loadingFinish,
      {
        response: cartResponse,
        reload: this.triggerCartReload,
        removing: this.itemsRemoving,
        subtotal: cartResponse.original_total_price,
        recentItem: this.recentItem,
        error,
      },
    );

    this.triggerCartReload = false;
    this.itemsRemoving = 0;
  }

  _parseItemResponses(cartResponse) {
    const itemResponses = [];

    for (let i = 0; i < this.inProgressItemRequests.length; i += 1) {
      const itemRequest = this.inProgressItemRequests[i];
      const itemResponse = {
        variant: itemRequest.variant,
        quantity: 0,
        total: 0,
      };

      for (let j = 0; j < cartResponse.items.length; j += 1) {
        const item = cartResponse.items[j];

        if (itemRequest.variant === item.variant_id) {
          itemResponse.variant = item.variant_id;
          itemResponse.quantity = item.quantity;
          itemResponse.total = item.original_line_price;
        }
      }

      itemResponses.push(itemResponse);
    }

    return itemResponses;
  }

  _handleResponse(cartResponse) {
    const itemResponses = this._parseItemResponses(cartResponse);

    this._triggerFinishLoadingEvent(cartResponse, itemResponses);

    this.inProgressItemRequests = [];
    this.lockSubmission = false;

    if (this.handlerHelpers.onResponse) {
      this.handlerHelpers.onResponse(cartResponse);
    }
  }

  _handleError(error) {
    const get = $.get({
      url: '/cart.js',
      success: null,
      dataType: 'json',
    });

    get.done((cartResponse) => {
      const itemResponses = this._parseItemResponses(cartResponse);

      if (this.handlerHelpers.onError) {
        this.handlerHelpers.onError(error);
      } else {
        this.bannerManager.add(
          error.responseJSON.description,
          Type.error,
          Priority.high,
          { autoDismiss: 5 },
        );
      }

      this._triggerFinishLoadingEvent(cartResponse, itemResponses, true);

      this.inProgressItemRequests = [];
      this.lockSubmission = false;
    });
  }

  _shouldPostRequests() {
    return (
      !this.lockSubmission
      && (
        this.itemRequests.length > 0
        || this.noteRequest !== null
      ) && (
        Math.floor(Date.now())
        - this.lastRequestTimestamp
        >= this.postAfterLastRequestMillis
      )
    );
  }

  _postRequests() {
    if (!this._shouldPostRequests()) {
      return;
    }

    let post = null;
    const request = {
      note: this.noteRequest,
      updates: {},
    };

    this.itemRequests.forEach((itemRequest) => {
      request.updates[itemRequest.variant] = itemRequest.quantity;
    });

    this.inProgressItemRequests = this.itemRequests;
    this.lockSubmission = true;
    this.noteRequest = null;
    this.itemRequests = [];

    post = $.post({
      url: '/cart/update.js',
      data: request,
      success: () => {},
      error: () => {},
      dataType: 'json',
    });

    post
      .done(this.handleResponse)
      .fail(this.handleError);
  }

  _findExistingItemRequest(variant) {
    let itemRequest = null;

    this.itemRequests.forEach((iRequest) => {
      if (iRequest.variant === variant) {
        itemRequest = iRequest;
      }
    });

    return itemRequest;
  }
}
